import { useEffect, useCallback, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import useDoubleRequestSubmit from '../../../../../hooks/formHooks/useDoubleRequestSubmit'

import {
  clearCreateAssetsBatch,
  clearUpdateAssetGroup,
  createAssetsBatch,
  createAssetsBatchSuccess,
  updateAssetGroup,
  updateAssetGroupSuccess
} from '../../../../../modules/actions/assets'

import {
  assetsCreatedDataSelector,
  assetsCreateErrorSelector,
  assetsWasCreatedSelector,
  updateAssetGroupErrorSelector,
  updateAssetGroupWasUpdatedSelector
} from '../../../../../modules/selectors/assets'

import { isTextAssetType } from '../helpers'
import { formatTextValuesToAssetsCreation } from '../formatters'
import { formatAssetsToAssetGroupUpdate, formatImageAssetsToAssetGroupUpdate, formatUpdatedAssets } from './formatters'

import { FILE_URL } from '../../fields'
import { HISTORY_META_EDIT_SECTION } from '../../../../../constants/changeHistory'

export function useFixWarningModalRepresentation({ setFormikValues, initialValues }) {
  const assetGroupWasUpdated = useSelector(updateAssetGroupWasUpdatedSelector)

  useEffect(() => {
    // fix to set new initialValues to formik, as native formik reinitialize values is making too late so warning modal appear
    if (assetGroupWasUpdated) {
      setFormikValues(initialValues)
    }
  }, [setFormikValues, assetGroupWasUpdated, initialValues])
}

// GoogleAssetGroupEdit form submission consists from 2 different requests:
// createAssetsBatch for newly added assets and then updateAssetGroup once first successfully submitted the
// 2nd is triggerred
export function useEditAssetGroupSubmit({
  originalSectionAssets,
  fieldType,
  assetGroupId,
  initialValues,
  sectionValueKey,
  validationSchema,
  isInternalAssetGroup,
  title,
  adAccount
}) {
  const dispatch = useDispatch()

  const [newAssets, setNewAssets] = useState([])
  const [deleteAssets, setDeleteAssets] = useState([])

  // const assetsWasCreated = useSelector(assetsWasCreatedSelector)
  const createdAssetsData = useSelector(assetsCreatedDataSelector)

  const isTextAsset = useMemo(() => isTextAssetType(fieldType), [fieldType])

  const createNewTextAssets = useCallback(
    createAssets => {
      const formattedAssets = formatTextValuesToAssetsCreation({
        values: { [sectionValueKey]: createAssets },
        adAccountId: adAccount,
        allowIncludeYoutubeLink: true
      })
      // formatTextListToAsset
      dispatch(
        createAssetsBatch({
          account: adAccount,
          operations: formattedAssets
        })
      )
    },
    [dispatch, sectionValueKey, adAccount]
  )

  const createAssetsHandler = useCallback(
    values => {
      // each asset after update should have some "action": create/update/delete
      const { createAssets, deleteAssets } = formatUpdatedAssets(
        originalSectionAssets,
        values[sectionValueKey],
        isInternalAssetGroup
      )

      if (isTextAsset && !!createAssets.length) {
        createNewTextAssets(createAssets)
      } else {
        // filter not uploaded image, in case user pressed 'add one more image', but have not selected file
        const uploadedCreateAssets = createAssets.filter(item => {
          return !!item[FILE_URL]
        })
        setNewAssets(uploadedCreateAssets)

        // skip asset creation and run assetGroup update directly
        // image assets are already created when edit form is submitting
        dispatch(createAssetsBatchSuccess(createAssets))
      }

      setDeleteAssets(deleteAssets)
    },
    [dispatch, sectionValueKey, createNewTextAssets, originalSectionAssets, isTextAsset, isInternalAssetGroup]
  )

  const updateAssetGroupHandler = useCallback(
    values => {
      let formattedCreateAssets
      if (isTextAsset) {
        formattedCreateAssets = formatAssetsToAssetGroupUpdate(createdAssetsData.operations, fieldType, assetGroupId)
      } else {
        formattedCreateAssets = formatImageAssetsToAssetGroupUpdate(newAssets, fieldType, assetGroupId)
      }
      const updatedAssetsList = [...deleteAssets, ...formattedCreateAssets]

      if (!!updatedAssetsList.length) {
        dispatch(
          updateAssetGroup(
            {
              account: adAccount,
              assets: updatedAssetsList,
              ...(isInternalAssetGroup && { internal: true }),
              meta: {
                [HISTORY_META_EDIT_SECTION]: title
              }
            },
            assetGroupId
          )
        )
      } else {
        // ignore sending the request if the assets list is empty and dispatch success update for trigering all successSubmit actions i.e. closing form, clearing data and etc..
        dispatch(updateAssetGroupSuccess({}))
      }
    },
    [
      dispatch,
      isTextAsset,
      fieldType,
      adAccount,
      deleteAssets,
      createdAssetsData,
      newAssets,
      assetGroupId,
      isInternalAssetGroup,
      title
    ]
  )

  const clearSubmitHandler = useCallback(() => {
    dispatch(clearCreateAssetsBatch())
    dispatch(clearUpdateAssetGroup())
  }, [dispatch])

  const createAssetsRequest = useMemo(
    () => ({
      requestHandler: createAssetsHandler,
      errorSelector: assetsCreateErrorSelector,
      successSelector: assetsWasCreatedSelector,
      dataSelector: assetsCreatedDataSelector
    }),
    [createAssetsHandler]
  )

  const updateAssetGroupRequest = useMemo(
    () => ({
      requestHandler: updateAssetGroupHandler,
      errorSelector: updateAssetGroupErrorSelector,
      successSelector: updateAssetGroupWasUpdatedSelector
    }),
    [updateAssetGroupHandler]
  )

  const requests = useMemo(
    () => [createAssetsRequest, updateAssetGroupRequest],
    [createAssetsRequest, updateAssetGroupRequest]
  )

  const { onSubmit, onClearSubmit, isLoadingSelector, errorSelector, successSubmit } = useDoubleRequestSubmit({
    requests,
    clearSubmitHandler
  })

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit,
    validationSchema
  })
  useFixWarningModalRepresentation({ setFormikValues: formik.setValues, initialValues })

  return {
    purifiedFormik: usePurifiedFormik(formik),
    onClearSubmit,
    isLoadingSelector,
    errorSelector,
    successSubmit
  }
}
